import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// @mui
import { styled } from '@mui/material/styles';
import {
  Stack,
  Typography,
  Grid,
  Container,
  Card,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
// components
import Iconify from '../components/iconify';
import PdfComp from '../components/pdf-reader/index';
import Label from '../components/label';
// context
import { EntityContext } from '../entity';
import { AccountContext } from '../account';
// ----------------------------------------------------------------------


const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

// ----------------------------------------------------------------------

export default function BatchSignaturePage() {
  const { batchEnvelopeSignerKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [batchSignature, setBatchSignature] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { getBatchSignatureDetails } = useContext(EntityContext);
  const { getSession } = useContext(AccountContext);
  const getSessionRef = useRef(getSession);
  
  // Atualizar ref quando getSession mudar
  useEffect(() => {
    getSessionRef.current = getSession;
  }, [getSession]);

  const loadBatchSignature = async () => {
    if (!batchEnvelopeSignerKey) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // Para assinatura em lote, não precisamos de token pois é uma página pública
      const data = await getBatchSignatureDetails(undefined, batchEnvelopeSignerKey);
      
      if (data && typeof data === 'object') {
        setBatchSignature(data);
        // Não selecionar envelope automaticamente - usuário deve clicar
      } else {
        setBatchSignature(null);
      }
    } catch (error) {
      console.error('Error loading batch signature:', error);
      setBatchSignature(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (batchEnvelopeSignerKey) {
      loadBatchSignature();
    }
  }, [batchEnvelopeSignerKey]);

  // Verificar autenticação quando batchSignature for carregado
  useEffect(() => {
    if (batchSignature) {
      // Verificar autenticação sempre (tanto para is_observer true quanto false)
      getSessionRef.current()
        .then(() => {
          setIsAuthenticated(true);
        })
        .catch(() => {
          setIsAuthenticated(false);
          // Mostrar modal se não logado (em ambos os casos)
          setShowLoginModal(true);
        });
    }
  }, [batchSignature]);

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  const handleBack = () => {
    navigate('/dashboard/pendingSignatures');
  };

  const handleLogin = () => {
    // Salvar URL atual para redirecionamento após login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    // Navegar para página de login
    navigate('/login');
  };


  const handleSignatureAction = async () => {
    if (batchSignature?.signature_type === 'icp') {
      setError(null);
      const icpPath = location.pathname.startsWith('/endorsement')
        ? `/endorsement/${batchEnvelopeSignerKey}/batch/icp`
        : `/signature/${batchEnvelopeSignerKey}/batch/icp`;
      navigate(icpPath);
    } else {
      // a tela de confirmação pede o token e mostra canal, contagem e erros (TokenChallenge)
      navigate(`/endorsement/${batchEnvelopeSignerKey}/batch/token`, { state: { requestToken: true } });
    }
  };

  if (loading) {
    return (
      <StyledRoot>
        <Main>
          <Container maxWidth="xl">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <CircularProgress />
            </Box>
          </Container>
        </Main>
      </StyledRoot>
    );
  }

  if (!batchSignature) {
    return (
      <StyledRoot>
        <Main>
          <Container maxWidth="xl">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <Typography variant="h6">Lote de assinatura não encontrado</Typography>
            </Box>
          </Container>
        </Main>
      </StyledRoot>
    );
  }

  return (
    <StyledRoot>
      <Main>
        <Helmet>
          <title>Assinatura em Lote - {batchSignature.batch_envelope_name || 'Lote de Assinatura'}</title>
        </Helmet>

        <Container maxWidth="xl">
          {/* Header Customizado */}
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 1000, 
            bgcolor: 'background.default',
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton onClick={handleBack} color="inherit">
                  <Iconify icon="eva:arrow-back-fill" />
                </IconButton>
                <Typography variant="h4">
                  {batchSignature.batch_envelope_name || 'Lote de Assinatura'}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Label color="info">
                  {batchSignature.signature_type || 'email'}
                </Label>
                <Typography variant="body2" color="text.secondary">
                  {batchSignature.envelopes?.length || 0} envelopes
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Informações do lote */}
          <Card sx={{ mb: 2, p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Chave do Lote
                </Typography>
                <Typography variant="body1">
                  {batchSignature.batch_envelope_key || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tipo de Assinatura
                </Typography>
                <Typography variant="body1">
                  {batchSignature.signature_type || 'email'}
                </Typography>
              </Grid>
            </Grid>
          </Card>

          {/* Conteúdo principal */}
          <Grid container spacing={3}>
            {/* Visualizador de PDF */}
            <Grid item xs={12} lg={8}>
              <Card sx={{ height: '70vh', overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">
                    {selectedDocument ? selectedDocument.envelope_name : 'Selecione um envelope'}
                  </Typography>
                </Box>
                <Box sx={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
                  {selectedDocument ? (
                    selectedDocument.envelope_url ? (
                      <PdfComp pdfFile={selectedDocument.envelope_url} enableSignaturePlacement={batchSignature?.signature_type === 'icp'} />
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                        <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
                          Carregando documento...
                        </Typography>
                      </Box>
                    )
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body1" color="text.secondary">
                        Clique em algum registro para mostrar o conteúdo
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Lista de documentos */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: '70vh' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">
                    Envelopes do Lote
                  </Typography>
                </Box>
                <Box sx={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
                  <List>
                    {batchSignature.envelopes?.map((envelope, index) => (
                      <div key={envelope.envelope_key || index}>
                        <ListItem 
                          button 
                          selected={selectedDocument?.envelope_key === envelope.envelope_key}
                          onClick={() => handleDocumentSelect(envelope)}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'primary.lighter',
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Iconify icon="eva:file-text-fill" />
                          </ListItemIcon>
                          <ListItemText
                            primary={envelope.envelope_name}
                            secondary={
                              <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                                <Chip 
                                  label="Pendente" 
                                  size="small" 
                                  color="warning"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {envelope.envelope_key}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItem>
                        {index < batchSignature.envelopes.length - 1 && <Divider />}
                      </div>
                    ))}
                  </List>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Botão de Assinatura - Oculto quando is_observer for true */}
          {!batchSignature?.is_observer && (
          <Card sx={{ mt: 3, p: 3 }}>
            <Stack spacing={3}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                <Typography variant="h6">
                  Pronto para assinar este lote?
                </Typography>
              </Stack>

              {batchSignature?.signature_type === 'icp' && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Na próxima tela você escolherá o certificado digital e concluirá a assinatura ICP.
                </Typography>
              )}

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={handleSignatureAction}
                  startIcon={<Iconify icon="eva:edit-fill" />}
                >
                  {batchSignature?.signature_type === 'icp' ? 'Continuar para assinatura' : 'Assinar Lote'}
                </Button>
              </Stack>
            </Stack>
          </Card>
          )}
        </Container>
      </Main>

      {/* Modal de login para usuários não autenticados */}
      <Dialog 
        open={showLoginModal} 
        onClose={() => {}} 
        disableEscapeKeyDown
        hideBackdrop={false}
      >
        <DialogTitle>Login Necessário</DialogTitle>
        <DialogContent>
          <Typography>
            {batchSignature?.is_observer 
              ? 'Você precisa estar logado para acessar este lote de assinatura como observador.'
              : 'Você precisa estar logado para assinar este lote de documentos.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogin} variant="contained">
            Login
          </Button>
        </DialogActions>
      </Dialog>
    </StyledRoot>
  );
} 